import { get, noop } from 'lodash-es';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import {
	getAllOrders,
	getSipOrders,
	modifySipData,
	setAllOrders,
	setSipOrders,
	cancelSelectedOpenOrders,
	getSelectedOpenOrders,
	getSipOrderTransactions,
	setSipOrderTransactions,
	cancelOrder,
	fetchTradeDetails,
	setTradeDetails,
	getSelectOrderDetails,
	fetchSingleOrderDetails,
	setSelectOrderDetails,
	setModifiedSipData,
	getOpenOrdersData,
	setOrderTabLoader,
	setOrdersLoadingState,
	getAllOrdersThrottled,
} from '../../reducers/equity/orderBook';
import {
	cancel_all_orders,
	fetch_order_book,
	fetch_trade_book,
	fetch_all_sip_orders,
	modify_equity_sip,
	fetch_sip_transactions,
	fetch_single_order_details,
	cancel_order,
} from '../../../apis/equity/orderBook';
import orderBookLandingService from '../../../services/equity/orderBook/orderBookLandingService';
import { isEmptyOrUndefined } from '../../../utils/common/validators';
import {
	setActionLoaderFalse,
	setActionLoaderTrue,
} from '../../reducers/actionLoader';
import STRINGS from '../../../strings';
import {throttledActionGenerator} from "./throttledActionGenerator";

const REFRESH_THROTTLE_TIME = 1000;

export default function* sagaOrderBook() {
	yield takeLatest(getAllOrders.toString(), handleGetAllOrders);
	yield takeLatest(getSipOrders.toString(), handleGetSipOrders);
	yield takeLatest(modifySipData.toString(), handleModifySipData);
	yield takeLatest(
		getSipOrderTransactions.toString(),
		handleGetSipOrderTransactions,
	);
	yield takeLatest(cancelOrder.toString(), handelCancelOrder);
	yield takeLatest(
		cancelSelectedOpenOrders.toString(),
		handelCancelSelectedOpenOrders,
	);
	yield takeLatest(fetchTradeDetails.toString(), handleFetchTradeDetails);
	yield takeLatest(
		fetchSingleOrderDetails.toString(),
		handleFetchSingleOrderDetails,
	);
	yield throttledActionGenerator(getAllOrdersThrottled, getAllOrders, REFRESH_THROTTLE_TIME);
}

function* handleGetSipOrders({ payload = {} }) {
	const {
		sagaCallback = noop,
		token = '',
		isPullRefresh = false,
		Api,
	} = payload;
	try {
		if (!isPullRefresh) {
			yield put(setSipOrders({ isLoading: true, isError: false }));
		}
		const response = yield call(fetch_all_sip_orders, Api, {}, token);
		const data = response?.data;
		yield put(setSipOrders({ data, isLoading: false, isError: false }));
		yield call(sagaCallback, isPullRefresh);
	} catch (err) {
		console.log('error in handleGetSipOrders is', err);
		if (err !== STRINGS.COMMON.ApiCancelled) {
			yield put(setSipOrders({ isLoading: true, isError: true }));
		}
		yield call(sagaCallback, isPullRefresh, err.message);
	}
}

function* handleGetAllOrders({ payload = {} }) {
	const {
		sagaCallback = noop,
		isPullRefresh = false,
		Api,
		isTabLoading = false,
	} = payload;
	try {
		yield put(setOrdersLoadingState({ isError: false }));
		if (isTabLoading) {
			yield put(setOrdersLoadingState({ isLoading: false, isError: false }));
			yield put(setOrderTabLoader(true));
		}
		if (!isPullRefresh) {
			yield put(setOrdersLoadingState({ isLoading: true, isError: false }));
		}
		const response = yield call(fetch_order_book, Api, {});
		const data = response?.data;
		yield put(setAllOrders({ data }));
		yield put(setOrdersLoadingState({ isLoading: false, isError: false }));
		yield call(sagaCallback, data, isPullRefresh);
		// yield put(setOrderTabLoader(false));
	} catch (err) {
		console.error('error in handleGetAllOrders', err.message);
		yield put(setOrderTabLoader(false));
		yield put(setOrdersLoadingState({ isLoading: true, isError: true }));
		yield call(sagaCallback, isPullRefresh, err.message);
	}
}

function* handleModifySipData({ payload }) {
	const { sipOrderDetails = {}, sagaCallBack = noop, OdinApi, Api } = payload;
	try {
		yield put(setModifiedSipData({ isLoading: true, isError: false }));
		yield call(modify_equity_sip, OdinApi, sipOrderDetails);
		yield put(getSipOrders({ forceApiCall: true, Api }));
		yield put(setModifiedSipData({ isLoading: false, isError: false }));
		yield call(sagaCallBack);
	} catch (err) {
		yield put(setModifiedSipData({ isLoading: false, isError: true }));
		console.log('error in handleModifySipData is', err.message);
	}
}

function* handelCancelSelectedOpenOrders({ payload = {} }) {
	const { sagaCallback = noop, OdinApi } = payload;
	try {
		const selectedOrders = yield select(getSelectedOpenOrders);
		const openOrders = yield select(getOpenOrdersData);
		const cancelAll = selectedOrders.length === openOrders.length;
		const requestPayload = orderBookLandingService.getCancelOrdersPayload(
			selectedOrders,
			cancelAll,
		);
		const response = yield call(cancel_all_orders, OdinApi, requestPayload);
		yield call(sagaCallback, response);
	} catch (err) {
		console.log('error in handelCancelSelectedOpenOrders is', err.message);
		yield call(sagaCallback, { isFailed: true, message: err.message });
	}
}

function* handleGetSipOrderTransactions({ payload }) {
	const { sipId = '', OdinApi } = payload;
	try {
		yield put(setSipOrderTransactions({ isLoading: true }));
		const response = yield call(fetch_sip_transactions, OdinApi, sipId);
		const data = response?.data;
		yield put(setSipOrderTransactions({ data, isLoading: false }));
	} catch (err) {
		yield put(setSipOrderTransactions({ isLoading: false }));
		console.log('error in handleGetSipOrderTransactions is', err.message);
	}
}

function* handelCancelOrder({ payload = {} }) {
	const { sagaCallback = noop, OdinApi } = payload;
	try {
		const selectedOrder = yield select(getSelectOrderDetails);
		const response = yield call(
			cancel_order,
			OdinApi,
			selectedOrder.exchange,
			selectedOrder.order_id,
		);
		yield call(sagaCallback, response);
	} catch (err) {
		yield call(sagaCallback, { isFailed: true, message: err.message });
	}
}

function* handleFetchTradeDetails({ payload = {} }) {
	const { sagaCallback = noop, orderId, Api } = payload;
	try {
		yield put(setTradeDetails({ isLoading: true }));

		const response = yield call(fetch_trade_book, Api, orderId);
		const data = response?.data?.[orderId];
		yield put(setTradeDetails({ isLoading: false, data }));
	} catch (err) {
		yield call(sagaCallback, { isFailed: true, message: err.message });
	} finally {
		yield put(setTradeDetails({ isLoading: false }));
	}
}

function* handleFetchSingleOrderDetails({ payload = {} }) {
	const {
		sagaCallback = noop,
		orderId,
		Api,
		isPullToRefresh = false,
	} = payload;
	try {
		if (!isPullToRefresh) {
			yield put(setActionLoaderTrue(fetchSingleOrderDetails));
		}
		const response = yield call(fetch_single_order_details, Api, orderId);

		// After order placement/ modification, order can go in either executed or open state
		const excutedData = get(response, 'data.executed.data[0]', {});
		const openData = get(response, 'data.open.data[0]', {});
		if (!isEmptyOrUndefined(excutedData)) {
			yield put(setSelectOrderDetails(excutedData));
		} else if (!isEmptyOrUndefined(openData)) {
			yield put(setSelectOrderDetails(openData));
		}
		sagaCallback({ isFailed: false, message: '' });
	} catch (err) {
		console.error('err in handleFetchSingleOrderDetails', err.message);
		yield call(sagaCallback, { isFailed: true, message: err.message });
	} finally {
		yield put(setActionLoaderFalse(fetchSingleOrderDetails));
	}
}
